import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";

import { BASE_URL } from "../../components/config/config";
import AdminApi from "../../components/AdminApi/Api/api";

import {
  canUpdateSubscription,
  canViewSubscription,
} from "../../utility/permissionUtils";

/** Global price list only — no per-ticket amounts. */
const GLOBAL_PRICE_KEYS = [
  "annualsubscription",
  "frist_installment",
  "request_booking_amount",
];

/** Per primary-service price list (create/update with service_pricing). */
const SERVICE_PRICE_KEYS = [
  "annualsubscription",
  "frist_installment",
  "subscribed_user_ticket_amount",
  "unsubscribed_user_ticket_amount",
  "request_booking_amount",
];

const formatPriceFieldLabel = (key) => key.replace(/_/g, " ");

const toNumberOrEmpty = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? n : "";
};

// Allow only digits (non-negative integers) or empty string.
const toDigitsOrEmpty = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const s = String(value).trim();
  if (!/^\d+$/.test(s)) return "";
  const n = Number(s);
  return Number.isFinite(n) ? n : "";
};

// Prevent typing exponent/plus/minus/decimal so inputs stay integers.
const blockNonIntegerKeys = (e) => {
  const blocked = ["e", "E", "+", "-", "."];
  if (blocked.includes(e.key)) e.preventDefault();
};

const getTitleFromService = (service) =>
  service?.service_name || service?.title || service?.name || "";

const normalizeTitleForMatch = (title) => String(title ?? "").trim().toLowerCase();

const getRecordTitle = (r) => r?.title || r?.price?.title || "";

/** Backend may return Mongo `_id` or REST `id`. */
const getRecordDocumentId = (record) => {
  if (!record) return null;
  // Try common identifier field variants across different backend shapes.
  const id =
    record._id ??
    record.id ??
    record._doc?._id ??
    record._doc?.id ??
    record.price_list_id ??
    record.priceListId ??
    record.price_id ??
    record.priceId ??
    record.record_id ??
    record.ticket_price_id ??
    record.ticket_priceId ??
    record.ticketPriceId ??
    // Sometimes the id may be nested under `price` or other objects.
    record.price?._id ??
    record.price?.id ??
    record.ticket_price?._id ??
    record.ticket_price?.id ??
    record.price_list?._id ??
    record.price_list?.id;

  return id != null && id !== "" ? id : null;
};

const normalizePriceListDataToArray = (data) => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === "object") return [data];
  return [];
};

const getApiErrorMessage = (e) =>
  e?.response?.data?.message || e?.message || "Request failed";

const trimLabel = (v) => String(v ?? "").trim();

/**
 * Map get-category `names` item to parent category_id from price list
 * `categories[].services` or newer `service_pricing[].services`.
 */
const findCategoryIdFromPriceRecordCategories = (record, item) => {
  const l1 = trimLabel(item?.label ?? item?.level1 ?? item?.name);
  const l2 = trimLabel(item?.label2 ?? item?.level2 ?? item?.subCategory);

  const scanGroups = (groups) => {
    if (!Array.isArray(groups)) return null;
    for (const cat of groups) {
      const services = cat?.services;
      if (!Array.isArray(services)) continue;
      const hit = services.some((s) => {
        const sl1 = trimLabel(s?.label);
        const sl2 = trimLabel(s?.label2);
        return sl1 === l1 && sl2 === l2;
      });
      if (hit && cat.category_id != null && cat.category_id !== "") {
        return String(cat.category_id);
      }
    }
    return null;
  };

  const fromCategories = scanGroups(record?.categories);
  if (fromCategories) return fromCategories;

  return scanGroups(record?.service_pricing);
};

const getServicePricingFromRecord = (record) => {
  if (!record) return [];
  return (
    record.service_pricing ||
    record.servicePricing ||
    record.pricing?.service_pricing ||
    []
  );
};

/**
 * Filtered get-all-price-list responses often omit `categories`; full list from init usually has them.
 */
const findCategoryIdFromAllPriceListForPrimary = (primaryServiceId, item, records) => {
  if (!Array.isArray(records) || primaryServiceId == null || primaryServiceId === "") {
    return null;
  }
  for (const r of records) {
    const psMatch =
      String(r?.primary_service_id ?? "") === String(primaryServiceId) ||
      getServicePricingFromRecord(r).some(
        (p) => String(p?.primary_service_id) === String(primaryServiceId)
      );
    if (!psMatch) continue;
    const found = findCategoryIdFromPriceRecordCategories(r, item);
    if (found) return found;
  }
  return null;
};

/**
 * Per-line id for `service_pricing` rows: backend expects the subcategory line document id
 * (same shape as create), not a shared parent_category_id.
 */
const resolveRowCategoryId = (item, record, primaryServiceId, allPriceListRecords) => {
  if (item?._id != null && item._id !== "") {
    return String(item._id);
  }
  if (item?.id != null && item.id !== "") {
    return String(item.id);
  }
  if (item?.category_id != null && item.category_id !== "") {
    return String(item.category_id);
  }
  if (item?.parent_category_id != null && item.parent_category_id !== "") {
    return String(item.parent_category_id);
  }
  const fromRecord = findCategoryIdFromPriceRecordCategories(record, item);
  if (fromRecord) return fromRecord;
  const fromAll = findCategoryIdFromAllPriceListForPrimary(
    primaryServiceId,
    item,
    allPriceListRecords
  );
  if (fromAll) return fromAll;
  return "";
};

function Subscription() {
  const globalState = useSelector((state) => state.AdminReducer);
  const userPermissions = globalState?.adminDetails?.role_id?.permissions;

  const hasViewAccess = canViewSubscription(userPermissions);
  const hasUpdateAccess = canUpdateSubscription(userPermissions);

  const LS = {
    selectedClientId: "subscription:selectedClientId",
    selectedClientQrId: "subscription:selectedClientQrId",
    selectedPrimaryServiceId: "subscription:selectedPrimaryServiceId",
    activeTab: "subscription:activeTab",
  };

  const safeGetLs = (key) => {
    try {
      return typeof window !== "undefined" ? window.localStorage.getItem(key) : null;
    } catch {
      return null;
    }
  };

  const safeSetLs = (key, value) => {
    try {
      if (typeof window !== "undefined") window.localStorage.setItem(key, value);
    } catch {
      // ignore
    }
  };

  const [loading, setLoading] = useState(true);
  const [priceListRecords, setPriceListRecords] = useState([]); // full records from GET get-all-price-list
  const [globalDefaultsRecord, setGlobalDefaultsRecord] = useState(null); // record with global ticket_price + possible service_pricing
  const [globalDefaults, setGlobalDefaults] = useState(null); // globalDefaultsRecord.price

  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState(() => safeGetLs(LS.selectedClientId) || "");
  const [selectedClientQrId, setSelectedClientQrId] = useState(
    () => safeGetLs(LS.selectedClientQrId) || ""
  );
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isLoadingCenterServices, setIsLoadingCenterServices] = useState(false);

  const [primaryServices, setPrimaryServices] = useState([]);
  const [selectedPrimaryServiceId, setSelectedPrimaryServiceId] = useState(
    () => safeGetLs(LS.selectedPrimaryServiceId) || ""
  );
  const selectedPrimaryService = useMemo(
    () =>
      primaryServices.find((s) => String(s._id) === String(selectedPrimaryServiceId)) ||
      null,
    [primaryServices, selectedPrimaryServiceId]
  );
  const selectedTitle = useMemo(
    () => getTitleFromService(selectedPrimaryService),
    [selectedPrimaryService]
  );

  const [existingRecordId, setExistingRecordId] = useState(null);
  const [hasExistingTopLevelRecord, setHasExistingTopLevelRecord] = useState(false);
  const [serviceLastUpdatedByName, setServiceLastUpdatedByName] = useState("");
  const [topLevelDefaults, setTopLevelDefaults] = useState(null); // fallback when inputs are blank
  const [activeTab, setActiveTab] = useState(() => safeGetLs(LS.activeTab) || "global"); // 'global' | 'service'

  const [globalTopLevelPrice, setGlobalTopLevelPrice] = useState({
    annualsubscription: "",
    frist_installment: "",
    request_booking_amount: "",
  });
  const [topLevelPrice, setTopLevelPrice] = useState({
    annualsubscription: "",
    frist_installment: "",
    subscribed_user_ticket_amount: "",
    unsubscribed_user_ticket_amount: "",
    request_booking_amount: "",
  });

  const [rows, setRows] = useState([]);

  const [isLoadingSubcats, setIsLoadingSubcats] = useState(false);
  const [isLoadingRowDefaults, setIsLoadingRowDefaults] = useState(false);
  const [isSavingGlobal, setIsSavingGlobal] = useState(false);
  const [isSavingService, setIsSavingService] = useState(false);

  const fetchClients = async () => {
    setIsLoadingClients(true);
    try {
      const res = await new AdminApi().getAllClients();
      const list = Array.isArray(res?.data) ? res.data : [];
      setClients(list);

      // If localStorage has a stale client id, fallback to first available client.
      const hasSelectedClient =
        selectedClientId &&
        list.some((c) => String(c?._id) === String(selectedClientId));
      if (!hasSelectedClient && list.length > 0) {
        const first = list[0];
        const fallbackClientId = first?._id ? String(first._id) : "";
        const fallbackQrId = first?.qr_details?.qr_id
          ? String(first.qr_details.qr_id)
          : "";
        setSelectedClientId(fallbackClientId);
        setSelectedClientQrId(fallbackQrId);
        safeSetLs(LS.selectedClientId, fallbackClientId);
        safeSetLs(LS.selectedClientQrId, fallbackQrId);
        safeSetLs(LS.selectedPrimaryServiceId, "");
      }

      // Auto-pick first client for usability.
      if ((!selectedClientId || selectedClientId === "") && list.length > 0) {
        const first = list[0];
        // API #3 expects client Mongo `_id`, not the human `client_id` (e.g. "CL-88").
        const firstClientId = first?._id ? String(first._id) : "";
        const firstQrId = first?.qr_details?.qr_id ? String(first.qr_details.qr_id) : "";
        setSelectedClientId(firstClientId);
        setSelectedClientQrId(firstQrId);
      }

      // If we restored a client id from localStorage but qrId wasn't restored, compute it now.
      if (selectedClientId && (!selectedClientQrId || selectedClientQrId === "")) {
        const matched = list.find((c) => String(c?._id) === String(selectedClientId));
        const qrId = matched?.qr_details?.qr_id ? String(matched.qr_details.qr_id) : "";
        if (qrId) setSelectedClientQrId(qrId);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load clients");
      setClients([]);
    } finally {
      setIsLoadingClients(false);
    }
  };

  const findRecordByTitle = (title) => {
    if (!title) return null;
    const normalized = normalizeTitleForMatch(title);
    return (
      priceListRecords.find(
        (r) => normalizeTitleForMatch(getRecordTitle(r)) === normalized
      ) || null
    );
  };

  const extractServicePricingArray = (record) => {
    if (!record) return [];

    const primaryServiceId = record?.primary_service_id ?? "";

    // Backend shape can vary; try a few common keys first.
    const direct =
      record.service_pricing ||
      record.servicePricing ||
      record.pricing?.service_pricing;

    if (Array.isArray(direct) && direct.length > 0) {
      const first = direct[0];
      // New GET shape: service_pricing[] with nested services[] per category group
      if (first && Array.isArray(first.services)) {
        return direct.flatMap((group) => {
          const gid = group?.category_id ?? "";
          const services = group?.services;
          if (!Array.isArray(services)) return [];
          return services.map((s) => ({
            primary_service_id: primaryServiceId,
            category_id:
              s?._id != null && s._id !== ""
                ? String(s._id)
                : gid != null && gid !== ""
                  ? String(gid)
                  : "",
            level1: s?.label ?? s?.level1 ?? s?.name ?? "",
            level2: s?.label2 ?? s?.level2 ?? s?.subCategory ?? "",
            subscribed_price: s?.subscribed_price ?? s?.subscribed ?? null,
            unsubscribed_price: s?.unsubscribed_price ?? s?.unsubscribed ?? null,
          }));
        });
      }
      // Flat rows (create/update wire shape: one row per line item)
      return direct.map((p) => ({
        primary_service_id: p?.primary_service_id ?? primaryServiceId,
        category_id:
          p?.category_id != null && p?.category_id !== "" ? String(p.category_id) : "",
        level1: p?.level1 ?? "",
        level2: p?.level2 ?? "",
        subscribed_price: p?.subscribed_price ?? p?.subscribed ?? null,
        unsubscribed_price: p?.unsubscribed_price ?? p?.unsubscribed ?? null,
      }));
    }

    // Shape A: response returns `services[]` at the record root.
    if (Array.isArray(record?.services)) {
      const fallbackCategoryId =
        Array.isArray(record?.categories) && record.categories.length === 1
          ? record.categories[0]?.category_id
          : null;

      return record.services.map((s) => ({
        primary_service_id: primaryServiceId,
        category_id: s?.category_id ?? s?.categoryId ?? fallbackCategoryId ?? "",
        level1: s?.label ?? s?.level1 ?? s?.name ?? "",
        level2: s?.label2 ?? s?.level2 ?? s?.subCategory ?? "",
        subscribed_price: s?.subscribed_price ?? s?.subscribed ?? null,
        unsubscribed_price: s?.unsubscribed_price ?? s?.unsubscribed ?? null,
      }));
    }

    // Shape B: response returns `categories[].services[]`.
    const categories = record?.categories;
    if (Array.isArray(categories)) {
      return categories.flatMap((cat) => {
        const category_id = cat?.category_id;
        const services = cat?.services;
        if (!Array.isArray(services)) return [];

        return services.map((s) => ({
          primary_service_id: primaryServiceId,
          category_id,
          level1: s?.label ?? s?.level1 ?? s?.name ?? "",
          level2: s?.label2 ?? s?.level2 ?? s?.subCategory ?? "",
          subscribed_price: s?.subscribed_price ?? s?.subscribed ?? null,
          unsubscribed_price: s?.unsubscribed_price ?? s?.unsubscribed ?? null,
        }));
      });
    }

    return [];
  };

  const collectCategoryIdsFromPriceListRecordsForPrimary = (primaryServiceId, records) => {
    const ids = new Set();
    if (!Array.isArray(records) || primaryServiceId == null || primaryServiceId === "") {
      return ids;
    }
    for (const r of records) {
      const psMatch =
        String(r?.primary_service_id ?? "") === String(primaryServiceId) ||
        extractServicePricingArray(r).some(
          (p) => String(p?.primary_service_id) === String(primaryServiceId)
        );
      if (!psMatch) continue;
      const cats = r?.categories;
      if (Array.isArray(cats)) {
        for (const c of cats) {
          if (c?.category_id != null && c.category_id !== "") {
            ids.add(String(c.category_id));
          }
        }
      }
      const spGroups = r?.service_pricing;
      if (Array.isArray(spGroups)) {
        for (const g of spGroups) {
          if (g?.category_id != null && g.category_id !== "") {
            ids.add(String(g.category_id));
          }
          const svcs = g?.services;
          if (Array.isArray(svcs)) {
            for (const s of svcs) {
              if (s?._id != null && s._id !== "") ids.add(String(s._id));
              if (s?.category_id != null && s.category_id !== "") {
                ids.add(String(s.category_id));
              }
            }
          }
        }
      }
    }
    return ids;
  };

  const findPriceListRecordForPrimaryService = (primaryServiceId, title, records) => {
    if (!Array.isArray(records) || primaryServiceId == null || primaryServiceId === "") return null;

    // Prefer matching by `primary_service_id` first.
    const primaryMatches = [];
    const seenIds = new Set();
    for (const r of records) {
      const docId = getRecordDocumentId(r);

      const docPsMatch =
        r?.primary_service_id != null && String(r.primary_service_id) === String(primaryServiceId);
      const spPsMatch = extractServicePricingArray(r).some(
        (p) => p?.primary_service_id != null && String(p.primary_service_id) === String(primaryServiceId)
      );

      if (!docPsMatch && !spPsMatch) continue;

      if (docId) {
        const idStr = String(docId);
        if (seenIds.has(idStr)) continue;
        seenIds.add(idStr);
      }
      primaryMatches.push(r);
    }
    if (primaryMatches.length > 0) {
      if (title) {
        const normalized = normalizeTitleForMatch(title);
        const titleMatches = primaryMatches.filter(
          (r) => normalizeTitleForMatch(getRecordTitle(r)) === normalized
        );
        if (titleMatches.length > 0) {
          const withId = titleMatches.find((r) => !!getRecordDocumentId(r));
          return withId || titleMatches[0];
        }
      }

      // If title doesn't align (API uses different title), still return the primary-service match.
      const withId = primaryMatches.find((r) => !!getRecordDocumentId(r));
      return withId || primaryMatches[0];
    }

    // Fallback: match by title only.
    if (!title) return null;
    const normalized = normalizeTitleForMatch(title);
    const sameTitle = records.filter(
      (r) => normalizeTitleForMatch(getRecordTitle(r)) === normalized
    );
    if (sameTitle.length === 0) return null;

    const withId = sameTitle.find((r) => !!getRecordDocumentId(r));
    return withId || sameTitle[0];
  };

  const extractSubcategories = (res) => {
    const names =
      res?.data?.data?.names ||
      res?.data?.data?.categories ||
      res?.data?.names ||
      res?.data?.categories ||
      [];
    if (!Array.isArray(names)) return [];

    // Each item should look like: {_id,label,label2,...,category_service_prize}
    return names;
  };

  const filterServicePricingWithValidCategories = async (
    servicePricing,
    recordsForPriceList
  ) => {
    const list = Array.isArray(servicePricing) ? servicePricing : [];
    const primaryIds = [
      ...new Set(
        list.map((p) => p?.primary_service_id).filter((id) => id != null && id !== "")
      ),
    ];
    if (primaryIds.length === 0) return [];

    const recordsSource = recordsForPriceList ?? priceListRecords;
    const adminApi = new AdminApi();
    const validByPrimary = new Map();

    for (const psId of primaryIds) {
      try {
        const res = await adminApi.getaddedServiceCategory(psId);
        const names = extractSubcategories(res);
        const set = new Set();
        for (const n of names) {
          const lineId = String(n?._id || n?.id || "").trim();
          if (lineId) set.add(lineId);
          if (n?.category_id != null && n.category_id !== "") {
            set.add(String(n.category_id));
          }
          if (n?.parent_category_id != null && n.parent_category_id !== "") {
            set.add(String(n.parent_category_id));
          }
        }
        for (const cid of collectCategoryIdsFromPriceListRecordsForPrimary(
          psId,
          recordsSource
        )) {
          set.add(cid);
        }
        validByPrimary.set(String(psId), set);
      } catch (e) {
        console.error(e);
        validByPrimary.set(String(psId), new Set());
      }
    }

    return list.filter((p) => {
      const pid = String(p?.primary_service_id ?? "");
      const cid = String(p?.category_id ?? "");
      const set = validByPrimary.get(pid);
      return set && set.has(cid);
    });
  };

  const fetchAllPriceListRecords = async () => {
    const PAGE_SIZE = 100;
    let recordsArray = [];

    try {
      const merged = [];
      const seenIds = new Set();
      let skip = 0;

      while (true) {
        const response = await axios.get(`${BASE_URL}admin/get-all-price-list`, {
          params: { skip, limit: PAGE_SIZE },
        });
        const raw = response?.data?.data;
        const batch = normalizePriceListDataToArray(raw);
        const beforeLen = merged.length;

        for (const r of batch) {
          const rid = getRecordDocumentId(r);
          const id = rid != null ? String(rid) : null;
          if (id) {
            if (seenIds.has(id)) continue;
            seenIds.add(id);
          }
          merged.push(r);
        }

        if (batch.length < PAGE_SIZE) break;
        if (merged.length === beforeLen) break;
        skip += PAGE_SIZE;
      }

      recordsArray = merged;
    } catch (e) {
      console.error(e);
      const response = await axios.get(`${BASE_URL}admin/get-all-price-list`);
      recordsArray = normalizePriceListDataToArray(response?.data?.data);
    }

    setPriceListRecords(recordsArray);

    const globalRecord =
      recordsArray.find(
        (r) => normalizeTitleForMatch(getRecordTitle(r)) === "ticket_price"
      ) || null;
    setGlobalDefaultsRecord(globalRecord);
    setGlobalDefaults(globalRecord?.price || null);
    const globalPrice = globalRecord?.price || {};
    setGlobalTopLevelPrice({
      annualsubscription:
        globalPrice.annualsubscription !== undefined &&
        globalPrice.annualsubscription !== null
          ? globalPrice.annualsubscription
          : "",
      frist_installment:
        globalPrice.frist_installment !== undefined &&
        globalPrice.frist_installment !== null
          ? globalPrice.frist_installment
          : "",
      request_booking_amount:
        globalPrice.request_booking_amount !== undefined &&
        globalPrice.request_booking_amount !== null
          ? globalPrice.request_booking_amount
          : "",
    });

    return recordsArray;
  };

  useEffect(() => {
    const init = async () => {
      if (!hasViewAccess) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        await Promise.all([fetchAllPriceListRecords(), fetchClients()]);
      } catch (e) {
        console.error(e);
        toast.error("Failed to load subscription pricing data");
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [hasViewAccess]);

  const fetchPrimaryServicesForClient = async (qrId) => {
    setIsLoadingCenterServices(true);
    try {
      if (!qrId) {
        setPrimaryServices([]);
        setSelectedPrimaryServiceId("");
        return [];
      }

      const servicesRes = await axios.get(`${BASE_URL}admin/get-all-center-services`, {
        params: { qr_id: qrId },
      });

      const services = Array.isArray(servicesRes?.data?.data)
        ? servicesRes.data.data
        : Array.isArray(servicesRes?.data?.data?.data)
          ? servicesRes.data.data.data
          : [];

      setPrimaryServices(services);

      // If persisted primary service is stale for this client, pick the first available.
      if (services.length > 0) {
        const isValid =
          selectedPrimaryServiceId &&
          services.some((s) => String(s?._id) === String(selectedPrimaryServiceId));
        if (!isValid) {
          const first = services.find((s) => s?._id && s._id !== "other") || services[0];
          setSelectedPrimaryServiceId(String(first?._id || ""));
        }
      } else if (!services.length) {
        setSelectedPrimaryServiceId("");
      }
      return services;
    } catch (e) {
      console.error(e);
      setPrimaryServices([]);
      toast.error("Failed to load primary services for selected client");
      return [];
    } finally {
      setIsLoadingCenterServices(false);
    }
  };

  // When selected client changes, refetch the primary services list.
  useEffect(() => {
    if (!hasViewAccess) return;
    if (!selectedClientQrId) {
      setPrimaryServices([]);
      setSelectedPrimaryServiceId("");
      return;
    }
    fetchPrimaryServicesForClient(selectedClientQrId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientQrId, hasViewAccess]);

  // Persist selections so that re-visiting the page keeps the same Client/Primary Service.
  useEffect(() => {
    safeSetLs(LS.selectedClientId, String(selectedClientId || ""));
    safeSetLs(LS.selectedClientQrId, String(selectedClientQrId || ""));
  }, [selectedClientId, selectedClientQrId]);

  useEffect(() => {
    safeSetLs(LS.selectedPrimaryServiceId, String(selectedPrimaryServiceId || ""));
  }, [selectedPrimaryServiceId]);

  useEffect(() => {
    safeSetLs(LS.activeTab, String(activeTab || "global"));
  }, [activeTab]);

  const cascadeResetForClientChange = () => {
    // Reset downstream state; selections will be re-populated by the effects.
    setPrimaryServices([]);
    setSelectedPrimaryServiceId("");
    setRows([]);
    setTopLevelDefaults(null);
    setHasExistingTopLevelRecord(false);
    setServiceLastUpdatedByName("");
    setTopLevelPrice({
      annualsubscription: "",
      frist_installment: "",
      subscribed_user_ticket_amount: "",
      unsubscribed_user_ticket_amount: "",
      request_booking_amount: "",
    });
  };

  const handleClientChange = (clientId) => {
    const client = clients.find((c) => String(c?._id) === String(clientId));
    const qrId = client?.qr_details?.qr_id ? String(client.qr_details.qr_id) : "";
    setSelectedClientId(clientId);
    setSelectedClientQrId(qrId);
    cascadeResetForClientChange();
    safeSetLs(LS.selectedClientId, String(clientId || ""));
    safeSetLs(LS.selectedClientQrId, String(qrId || ""));
    safeSetLs(LS.selectedPrimaryServiceId, "");
  };

  // Auto-select first primary service to make the page usable immediately.
  useEffect(() => {
    if (!primaryServices.length) return;
    if (selectedPrimaryServiceId) return;
    setSelectedPrimaryServiceId(primaryServices[0]?._id || "");
  }, [primaryServices, selectedPrimaryServiceId]);

  const resolveGlobalRowDefault = (categoryId, level1, level2) => {
    if (!globalDefaultsRecord) return { subscribed: null, unsubscribed: null };
    const globalServicePricing = extractServicePricingArray(globalDefaultsRecord);

    const match =
      globalServicePricing.find(
        (p) =>
          String(p?.category_id) === String(categoryId) &&
          (level1 ? p?.level1 === level1 : true) &&
          (level2 ? p?.level2 === level2 : true)
      ) ||
      globalServicePricing.find((p) => String(p?.category_id) === String(categoryId)) ||
      (level1 && level2
        ? globalServicePricing.find(
            (p) => p?.level1 === level1 && p?.level2 === level2
          )
        : null);

    return {
      subscribed: match?.subscribed_price ?? null,
      unsubscribed: match?.unsubscribed_price ?? null,
    };
  };

  const fetchRowDefaultsForLevel = async ({
    primary_service_id,
    title,
    level1,
    level2,
    category_id,
  }) => {
    // Default per sub-category. Backend example:
    // /admin/get-all-price-list?primary_service_id=<id>&level1=<>&level2=<> &skip=0&limit=10&default_price=true&title=<title>
    const resp = await axios.get(`${BASE_URL}admin/get-all-price-list`, {
      params: {
        primary_service_id,
        level1,
        level2,
        default_price: true,
        title,
      },
    });

    const data = resp?.data?.data;
    const recordsArray = Array.isArray(data) ? data : data ? [data] : [];
    const first = recordsArray[0] || null;
    const servicePricing = extractServicePricingArray(first);

    const match =
      servicePricing.find((p) => String(p?.category_id) === String(category_id)) ||
      null;

    return {
      subscribed: match?.subscribed_price ?? null,
      unsubscribed: match?.unsubscribed_price ?? null,
    };
  };

  // Fetch full default pricing for a primary service once.
  // Per your requirement, we call `get-all-price-list` using only `primary_service_id`
  // and then map the returned service_pricing/category->services into the row table.
  const fetchAllDefaultsForPrimaryService = async (primaryServiceId) => {
    const resp = await axios.get(`${BASE_URL}admin/get-all-price-list`, {
      params: { primary_service_id: primaryServiceId },
    });

    const data = resp?.data?.data;
    const recordsArray = normalizePriceListDataToArray(data);

    const allServicePricing = [];
    for (const r of recordsArray) {
      const sp = extractServicePricingArray(r);
      for (const p of sp) {
        const psId = p?.primary_service_id ?? r?.primary_service_id;
        if (psId != null && String(psId) !== String(primaryServiceId)) continue;
        allServicePricing.push(p);
      }
    }

    const byCategoryId = new Map();
    const byLevelKey = new Map();

    const levelKey = (l1, l2) =>
      `${trimLabel(l1).toLowerCase()}|${trimLabel(l2).toLowerCase()}`;

    for (const p of allServicePricing) {
      const cid = p?.category_id;
      const l1 = p?.level1 ?? "";
      const l2 = p?.level2 ?? "";
      const subscribed = p?.subscribed_price ?? null;
      const unsubscribed = p?.unsubscribed_price ?? null;

      if (cid != null && cid !== "") {
        byCategoryId.set(String(cid), { subscribed, unsubscribed });
      }

      const key = levelKey(l1, l2);
      if (!byLevelKey.has(key) && (trimLabel(l1) || trimLabel(l2))) {
        byLevelKey.set(key, { subscribed, unsubscribed });
      }
    }

    return { byCategoryId, byLevelKey };
  };

  const loadSelectedPrimaryService = async () => {
    if (!selectedPrimaryServiceId) return;
    if (!selectedClientId) return;

    const title = selectedTitle;
    const primaryServiceId = selectedPrimaryServiceId;

    let record = findPriceListRecordForPrimaryService(
      primaryServiceId,
      title,
      priceListRecords
    );

    if (!getRecordDocumentId(record)) {
      try {
        const resp = await axios.get(`${BASE_URL}admin/get-all-price-list`, {
          params: { primary_service_id: primaryServiceId },
        });
        const data = resp?.data?.data;
        const arr = normalizePriceListDataToArray(data);
        record =
          findPriceListRecordForPrimaryService(primaryServiceId, title, arr) ||
          arr[0] ||
          null;
      } catch (e) {
        console.error(e);
      }
    }

    if (!record) {
      record =
        priceListRecords.find(
          (r) =>
            r?.primary_service_id != null &&
            String(r.primary_service_id) === String(primaryServiceId)
        ) || findRecordByTitle(title);
    }

    setExistingRecordId(getRecordDocumentId(record));
    const topLevelHasRecord =
      !!record &&
      record?.price != null &&
      SERVICE_PRICE_KEYS.some((k) => record?.price?.[k] !== undefined && record?.price?.[k] !== null);
    setHasExistingTopLevelRecord(topLevelHasRecord);
    setServiceLastUpdatedByName(record?.updated_by_name ?? "");

    const existingPrice = record?.price || {};
    const fallbackTopPrice = record?.price || globalDefaults || null;
    setTopLevelDefaults(fallbackTopPrice);
    setTopLevelPrice((prev) => ({
      ...prev,
      annualsubscription:
        existingPrice.annualsubscription !== undefined &&
        existingPrice.annualsubscription !== null
          ? existingPrice.annualsubscription
          : "",
      frist_installment:
        existingPrice.frist_installment !== undefined &&
        existingPrice.frist_installment !== null
          ? existingPrice.frist_installment
          : "",
      subscribed_user_ticket_amount:
        existingPrice.subscribed_user_ticket_amount !== undefined &&
        existingPrice.subscribed_user_ticket_amount !== null
          ? existingPrice.subscribed_user_ticket_amount
          : "",
      unsubscribed_user_ticket_amount:
        existingPrice.unsubscribed_user_ticket_amount !== undefined &&
        existingPrice.unsubscribed_user_ticket_amount !== null
          ? existingPrice.unsubscribed_user_ticket_amount
          : "",
      request_booking_amount:
        existingPrice.request_booking_amount !== undefined &&
        existingPrice.request_booking_amount !== null
          ? existingPrice.request_booking_amount
          : "",
    }));

    // Fetch subcategories for selected primary service.
    setIsLoadingSubcats(true);
    setIsLoadingRowDefaults(false);
    setRows([]);

    try {
      const [serviceAreaRes, categoryRes] = await Promise.all([
        axios.get(
          `${BASE_URL}client/get-category-based-client-and-service`,
          {
            params: {
              primary_services: primaryServiceId,
              client_id: selectedClientId,
            },
          }
        ),
        new AdminApi().getaddedServiceCategory(primaryServiceId),
      ]);

      const formatted = serviceAreaRes?.data?.data?.formatted;
      const allowedCategoryIds = new Set();

      const formattedList = Array.isArray(formatted) ? formatted : [];
      for (const entry of formattedList) {
        const cats =
          entry?.serviceAreas?.categories ||
          entry?.serviceAreas?.cats ||
          entry?.categories ||
          [];
        if (!Array.isArray(cats)) continue;

        for (const cat of cats) {
          const cid =
            cat?.category_id ?? cat?._id ?? cat?.id ?? cat?.categoryId ?? "";
          if (cid !== "") allowedCategoryIds.add(String(cid));
        }
      }

      const allSubcategories = extractSubcategories(categoryRes);
      const subcategories =
        allowedCategoryIds.size === 0
          ? []
          : allSubcategories.filter((item) => {
              const keys = [item?._id, item?.id, item?.category_id, item?.parent_category_id]
                .filter((v) => v != null && v !== "")
                .map((v) => String(v));
              return keys.some((k) => allowedCategoryIds.has(k));
            });

      const existingServicePricing = extractServicePricingArray(record);

      const updatedByName = record?.updated_by_name ?? "";
      const updatedByRole = record?.updated_by_role ?? "";

      const mappedRows = subcategories.map((item) => {
        const category_id = resolveRowCategoryId(
          item,
          record,
          primaryServiceId,
          priceListRecords
        );
        const lineItemId = item?._id ?? item?.id;
        const level1 = item?.label || item?.level1 || item?.name || "";
        const level2 = item?.label2 || item?.level2 || item?.subCategory || "";

        const existing = existingServicePricing.find((p) => {
          const psOk =
            !p?.primary_service_id ||
            String(p.primary_service_id) === String(primaryServiceId);
          if (!psOk) return false;
          if (category_id && String(p?.category_id) === String(category_id)) return true;
          if (lineItemId != null && String(p?.category_id) === String(lineItemId)) {
            return true;
          }
          const pL1 = trimLabel(p?.level1).toLowerCase();
          const pL2 = trimLabel(p?.level2).toLowerCase();
          const sL1 = trimLabel(level1).toLowerCase();
          const sL2 = trimLabel(level2).toLowerCase();
          if (sL1 && sL2 && pL1 === sL1 && pL2 === sL2) {
            return true;
          }
          return false;
        });

        // Row "exists" only when backend has both subscribed & unsubscribed explicitly set.
        const hasSavedExplicitPrice =
          existing?.subscribed_price !== null &&
          existing?.subscribed_price !== undefined &&
          existing?.subscribed_price !== "" &&
          existing?.unsubscribed_price !== null &&
          existing?.unsubscribed_price !== undefined &&
          existing?.unsubscribed_price !== "";

        const globalRowDefaults = resolveGlobalRowDefault(
          category_id,
          level1,
          level2
        );

        return {
          category_id,
          /** Prefer for API payload — parent category the backend ties to primary service */
          parent_category_id:
            item?.parent_category_id != null && item.parent_category_id !== ""
              ? String(item.parent_category_id)
              : "",
          lineItem_id: lineItemId != null ? String(lineItemId) : "",
          level1,
          level2,
          subscribed_price: existing?.subscribed_price ?? "",
          unsubscribed_price: existing?.unsubscribed_price ?? "",
          hasSavedExplicitPrice,
          updatedByName,
          updatedByRole,
          default_subscribed_price: null, // filled below if needed
          default_unsubscribed_price: null, // filled below if needed
          global_subscribed_price: globalRowDefaults.subscribed,
          global_unsubscribed_price: globalRowDefaults.unsubscribed,
        };
      });

      setRows(mappedRows);

      const rowsNeedDefaults = mappedRows.filter(
        (r) => r.subscribed_price === "" || r.unsubscribed_price === ""
      );

      if (rowsNeedDefaults.length === 0) return;

      setIsLoadingRowDefaults(true);

      // Fetch all defaults once, then map them into the rows.
      let defaultsIndex = null;
      try {
        defaultsIndex = await fetchAllDefaultsForPrimaryService(primaryServiceId);
      } catch (e) {
        console.error("Failed fetching all primary defaults", e);
        // Keep defaults as null; save-time will fall back to global row defaults.
      }

      const updated = [...mappedRows];
      const makeLevelKey = (l1, l2) =>
        `${trimLabel(l1).toLowerCase()}|${trimLabel(l2).toLowerCase()}`;

      for (let i = 0; i < updated.length; i++) {
        const row = updated[i];
        const needsSubscribed = row.subscribed_price === "";
        const needsUnsubscribed = row.unsubscribed_price === "";
        if (!needsSubscribed && !needsUnsubscribed) continue;

        const byCategoryId = defaultsIndex?.byCategoryId;
        const byLevelKey = defaultsIndex?.byLevelKey;
        const def =
          (byCategoryId && row.category_id ? byCategoryId.get(String(row.category_id)) : null) ||
          (byCategoryId && row.lineItem_id ? byCategoryId.get(String(row.lineItem_id)) : null) ||
          (byLevelKey ? byLevelKey.get(makeLevelKey(row.level1, row.level2)) : null) ||
          null;

        updated[i] = {
          ...row,
          default_subscribed_price:
            needsSubscribed ? def?.subscribed ?? null : row.default_subscribed_price,
          default_unsubscribed_price:
            needsUnsubscribed ? def?.unsubscribed ?? null : row.default_unsubscribed_price,
        };
      }

      setRows(updated);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load sub-categories for selected service");
      setRows([]);
    } finally {
      setIsLoadingSubcats(false);
      setIsLoadingRowDefaults(false);
    }
  };

  useEffect(() => {
    if (!hasViewAccess) return;
    if (!selectedClientId) return;
    if (!selectedTitle || !selectedPrimaryServiceId) return;
    if (activeTab !== "service") return;
    loadSelectedPrimaryService();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedClientId,
    selectedPrimaryServiceId,
    selectedTitle,
    hasViewAccess,
    globalDefaultsRecord,
    activeTab,
  ]);

  const handleTopLevelChange = (e) => {
    if (!hasUpdateAccess) {
      toast.error("You don't have permission to update subscription pricing");
      return;
    }

    const { name, value } = e.target;
    setTopLevelPrice((prev) => ({
      ...prev,
      [name]: toDigitsOrEmpty(value),
    }));
  };

  const handleGlobalTopLevelChange = (e) => {
    if (!hasUpdateAccess) {
      toast.error("You don't have permission to update subscription pricing");
      return;
    }

    const { name, value } = e.target;
    setGlobalTopLevelPrice((prev) => ({
      ...prev,
      [name]: toDigitsOrEmpty(value),
    }));
  };

  const handleRowChange = (index, key, value) => {
    if (!hasUpdateAccess) return;
    setRows((prev) => {
      const next = [...prev];
      next[index] = {
        ...next[index],
        [key]: toDigitsOrEmpty(value),
      };
      return next;
    });
  };

  const generateDescription = (title, rowsList) => {
    const level1s = Array.from(
      new Set(rowsList.map((r) => r.level1).filter(Boolean))
    );
    const short = level1s.slice(0, 12).join(", ");
    const suffix = level1s.length > 12 ? ", ..." : "";
    return `${title} - ${short}${suffix}`;
  };

  const resolveTopLevelPriceFromInputs = () => {
    // Blank inputs use service record defaults, then global defaults.
    const resolvedTopPrice = {};
    for (const k of SERVICE_PRICE_KEYS) {
      const input = topLevelPrice[k];
      const resolved =
        input === "" || input === null || input === undefined
          ? topLevelDefaults?.[k] ?? globalDefaults?.[k]
          : Number(input);

      if (resolved === null || resolved === undefined || !Number.isFinite(resolved)) {
        return null;
      }

      resolvedTopPrice[k] = resolved;
    }
    return resolvedTopPrice;
  };

  /** Wire shape for create/update: no per-line primary_service_id (that lives on the root payload). */
  const normalizeServicePricingForApi = (items) => {
    if (!Array.isArray(items)) return [];
    return items
      .map((p) => ({
        category_id:
          p?.category_id != null && String(p.category_id).trim() !== ""
            ? String(p.category_id)
            : "",
        level1: p?.level1 ?? "",
        level2: p?.level2 ?? "",
        subscribed_price: Number(p?.subscribed_price),
        unsubscribed_price: Number(p?.unsubscribed_price),
      }))
      .filter(
        (row) =>
          row.category_id &&
          Number.isFinite(row.subscribed_price) &&
          Number.isFinite(row.unsubscribed_price)
      );
  };

  const buildServicePricingPayloadFromExplicitRows = () => {
    const payloadCategoryId = (row) => {
      if (row.lineItem_id != null && String(row.lineItem_id).trim() !== "") {
        return String(row.lineItem_id);
      }
      if (row.category_id != null && String(row.category_id).trim() !== "") {
        return String(row.category_id);
      }
      return "";
    };

    const built = rows
      .filter((row) => {
        const subOk = row.subscribed_price !== "" && row.subscribed_price !== null && row.subscribed_price !== undefined;
        const unsubOk =
          row.unsubscribed_price !== "" &&
          row.unsubscribed_price !== null &&
          row.unsubscribed_price !== undefined;
        if (!subOk || !unsubOk) return false;
        if (!Number.isFinite(Number(row.subscribed_price)) || !Number.isFinite(Number(row.unsubscribed_price))) {
          return false;
        }
        const cid = payloadCategoryId(row);
        return cid !== "";
      })
      .map((row) => {
        const cid = payloadCategoryId(row);
        return {
          category_id: cid,
          level1: row.level1,
          level2: row.level2,
          subscribed_price: Number(row.subscribed_price),
          unsubscribed_price: Number(row.unsubscribed_price),
        };
      });
    return normalizeServicePricingForApi(built);
  };

  const handleTopLevelSave = async () => {
    if (!hasUpdateAccess) {
      toast.error("You don't have permission to update subscription pricing");
      return;
    }
    if (!selectedPrimaryServiceId || !selectedTitle) {
      toast.error("Select a primary service first");
      return;
    }

    const resolvedTopPrice = resolveTopLevelPriceFromInputs();
    if (!resolvedTopPrice) {
      toast.error(
        "Missing resolved value for one or more top-level price fields (including subscribed and unsubscribed ticket amounts). Leave blank only when global defaults exist."
      );
      return;
    }

    const putPriceListUpdate = (id, body) => {
      const ps = String(body?.primary_service_id ?? "").trim();
      if (!ps) {
        return Promise.reject(new Error("Missing primary_service_id for price list update"));
      }
      return axios.put(
        `${BASE_URL}admin/updatePriceList/${id}`,
        { ...body, _id: id, primary_service_id: ps },
        {
          headers: {
            _id: String(id),
            primary_service_id: ps,
          },
        }
      );
    };

    try {
      setIsSavingService(true);
      let resolvedRecord =
        findPriceListRecordForPrimaryService(
          selectedPrimaryServiceId,
          selectedTitle,
          priceListRecords
        ) || null;
      let resolvedRecordId = getRecordDocumentId(resolvedRecord) || existingRecordId;

      // If prices already exist (UI says "Update") but id resolution failed,
      // re-fetch records to reliably find the id required by PUT /updatePriceList/:id.
      if (hasExistingTopLevelRecord && !resolvedRecordId) {
        try {
          const resp = await axios.get(`${BASE_URL}admin/get-all-price-list`, {
            params: { primary_service_id: selectedPrimaryServiceId },
          });
          const data = resp?.data?.data;
          const arr = normalizePriceListDataToArray(data);
          resolvedRecord =
            findPriceListRecordForPrimaryService(
              selectedPrimaryServiceId,
              selectedTitle,
              arr
            ) || resolvedRecord;
          resolvedRecordId = getRecordDocumentId(resolvedRecord) || null;
        } catch (e2) {
          console.error(e2);
        }
      }

      const recordIdForPrimaryLookup =
        resolvedRecordId || existingRecordId || getRecordDocumentId(resolvedRecord);
      const recordMatchingDocId =
        recordIdForPrimaryLookup != null && recordIdForPrimaryLookup !== ""
          ? priceListRecords.find(
              (r) => String(getRecordDocumentId(r)) === String(recordIdForPrimaryLookup)
            ) || null
          : null;

      const primaryServiceIdForPayload = String(
        selectedPrimaryServiceId ||
          resolvedRecord?.primary_service_id ||
          recordMatchingDocId?.primary_service_id ||
          ""
      ).trim();

      if (!primaryServiceIdForPayload) {
        toast.error("Missing primary service id. Select a primary service and try again.");
        return;
      }

      const payload = {
        title: selectedTitle,
        description: generateDescription(selectedTitle, rows),
        primary_service_id: primaryServiceIdForPayload,
        service_pricing: buildServicePricingPayloadFromExplicitRows(),
        price: resolvedTopPrice,
      };

      if (hasExistingTopLevelRecord) {
        if (!resolvedRecordId) {
          toast.error(
            "Could not resolve price list id for update. Refresh the page and try again."
          );
          return;
        }
        await putPriceListUpdate(resolvedRecordId, payload);
      } else {
        await axios.post(`${BASE_URL}admin/create-price-list`, payload);
      }

      toast.success("Service and sub-category pricing saved successfully");
      await fetchAllPriceListRecords();
      await loadSelectedPrimaryService();
    } catch (e) {
      console.error(e);
      const status = e?.response?.status;
      const msg = String(e?.response?.data?.message || "");
      const titleExists =
        status === 410 ||
        status === 409 ||
        msg.toLowerCase().includes("title already exists") ||
        msg.toLowerCase().includes("already exists");

      if (hasExistingTopLevelRecord && titleExists) {
        try {
          const refreshed = await fetchAllPriceListRecords();
          const rec = findPriceListRecordForPrimaryService(
            selectedPrimaryServiceId,
            selectedTitle,
            refreshed
          );
          const recId = getRecordDocumentId(rec);
          if (!recId) {
            toast.error(
              "Could not resolve price list id for update. Refresh the page and try again."
            );
            return;
          }
          const retryPayload = {
            title: selectedTitle,
            description: generateDescription(selectedTitle, rows),
            primary_service_id: String(
              selectedPrimaryServiceId || rec?.primary_service_id || ""
            ).trim(),
            service_pricing: buildServicePricingPayloadFromExplicitRows(),
            price: resolvedTopPrice,
          };
          if (!retryPayload.primary_service_id) {
            toast.error("Missing primary service id. Refresh and try again.");
            return;
          }
          await putPriceListUpdate(recId, retryPayload);
          toast.success("Service and sub-category pricing saved successfully");
          await fetchAllPriceListRecords();
          await loadSelectedPrimaryService();
          return;
        } catch (e2) {
          console.error(e2);
          toast.error(getApiErrorMessage(e2));
          return;
        }
      }

      toast.error(getApiErrorMessage(e));
    } finally {
      setIsSavingService(false);
    }
  };

  const handleGlobalSave = async () => {
    if (!hasUpdateAccess) {
      toast.error("You don't have permission to update subscription pricing");
      return;
    }
    if (!globalDefaults) {
      toast.error("Global defaults not loaded yet");
      return;
    }

    const resolvedPrice = {};
    for (const k of GLOBAL_PRICE_KEYS) {
      const input = globalTopLevelPrice[k];
      const resolved =
        input === "" || input === null || input === undefined
          ? globalDefaults?.[k]
          : Number(input);

      if (
        resolved === null ||
        resolved === undefined ||
        !Number.isFinite(resolved)
      ) {
        toast.error(`Missing resolved value for "${k}". Please fill it in.`);
        return;
      }

      resolvedPrice[k] = resolved;
    }

    const title = globalDefaultsRecord?.title || "ticket_price";
    const description = `${title} - Global pricing defaults`;

    const baseGlobalPayload = {
      title,
      description,
      price: resolvedPrice,
      service_pricing: [],
    };

    const putGlobalPriceList = async (record, recordsForFilter, payloadBase) => {
      const rawServicePricing = extractServicePricingArray(record);
      const filtered = await filterServicePricingWithValidCategories(
        rawServicePricing,
        recordsForFilter
      );
      const service_pricing = normalizeServicePricingForApi(filtered);
      const payload = {
        ...payloadBase,
        ...(record?.primary_service_id != null &&
        String(record.primary_service_id).trim() !== ""
          ? { primary_service_id: String(record.primary_service_id) }
          : {}),
        service_pricing,
      };
      const globalId = getRecordDocumentId(record);
      if (!globalId) {
        throw new Error("Could not resolve price list id for global update.");
      }
      await axios.put(
        `${BASE_URL}admin/updatePriceList/${globalId}`,
        { ...payload, _id: globalId },
        { headers: { _id: globalId } }
      );
    };

    try {
      setIsSavingGlobal(true);

      if (globalDefaultsRecord) {
        await putGlobalPriceList(globalDefaultsRecord, priceListRecords, baseGlobalPayload);
      } else {
        const rawServicePricing = extractServicePricingArray(globalDefaultsRecord);
        const filtered = await filterServicePricingWithValidCategories(
          rawServicePricing,
          priceListRecords
        );
        const service_pricing = normalizeServicePricingForApi(filtered);
        await axios.post(`${BASE_URL}admin/create-price-list`, {
          ...baseGlobalPayload,
          ...(globalDefaultsRecord?.primary_service_id != null &&
          String(globalDefaultsRecord.primary_service_id).trim() !== ""
            ? { primary_service_id: String(globalDefaultsRecord.primary_service_id) }
            : {}),
          service_pricing,
        });
      }

      toast.success("Global pricing saved successfully");
      await fetchAllPriceListRecords();
    } catch (e) {
      console.error(e);
      const status = e?.response?.status;
      const msg = String(e?.response?.data?.message || "");
      const titleExists =
        status === 410 || msg.toLowerCase().includes("title already exists");

      if (!getRecordDocumentId(globalDefaultsRecord) && titleExists) {
        try {
          const refreshed = await fetchAllPriceListRecords();
          const gr =
            refreshed.find(
              (r) =>
                normalizeTitleForMatch(getRecordTitle(r)) ===
                normalizeTitleForMatch(title)
            ) || null;
          if (gr) {
            await putGlobalPriceList(gr, refreshed, baseGlobalPayload);
            toast.success("Global pricing saved successfully");
            await fetchAllPriceListRecords();
            return;
          }
        } catch (e2) {
          console.error(e2);
          toast.error(getApiErrorMessage(e2));
          return;
        }
      }

      toast.error(getApiErrorMessage(e));
    } finally {
      setIsSavingGlobal(false);
    }
  };

  if (!hasViewAccess) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          color: "red",
          fontSize: "1.5rem",
          fontWeight: "bold",
        }}
      >
        Access Denied - You don't have permission to view subscription details
      </div>
    );
  }

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
          color: "#68d3bd",
          fontSize: "1.2rem",
        }}
      >
        Loading subscription pricing data...
      </div>
    );
  }

  return (
    <div
      style={{
        border: "2px solid white",
        backgroundColor: "#f9f9f9",
        display: "flex",
        flexDirection: "column",
        width: "80vw",
        maxWidth: 1200,
        margin: "auto",
        gap: "1rem",
        color: "black",
        padding: "2rem",
        borderRadius: "12px",
        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <h1 style={{ margin: 0 }}>Subscription Pricing Management</h1>
        {!hasUpdateAccess && (
          <span
            style={{
              color: "orange",
              fontSize: "0.9rem",
              fontStyle: "italic",
              backgroundColor: "#fff3cd",
              padding: "0.5rem",
              borderRadius: "4px",
              border: "1px solid #ffeaa7",
            }}
          >
            View Only
          </span>
        )}
      </div>

      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          marginTop: 8,
        }}
      >
        <button
          onClick={() => setActiveTab("global")}
          type="button"
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: `1px solid ${activeTab === "global" ? "#68d3bd" : "#ccc"}`,
            backgroundColor: activeTab === "global" ? "#68d3bd" : "#fff",
            color: activeTab === "global" ? "white" : "black",
            cursor: "pointer",
            fontWeight: 700,
          }}
        >
          Global Price
        </button>
        <button
          onClick={() => setActiveTab("service")}
          type="button"
          style={{
            padding: "10px 14px",
            borderRadius: 10,
            border: `1px solid ${activeTab === "service" ? "#68d3bd" : "#ccc"}`,
            backgroundColor: activeTab === "service" ? "#68d3bd" : "#fff",
            color: activeTab === "service" ? "white" : "black",
            cursor: "pointer",
            fontWeight: 700,
          }}
        >
          Service and sub-category Wise Price
        </button>
      </div>

      {activeTab === "global" && (
        <div
          style={{
            border: "1px solid #e3e3e3",
            borderRadius: 12,
            padding: 16,
            backgroundColor: "#fff",
          }}
        >
          <h3 style={{ marginTop: 0, marginBottom: 12 }}>Global Price</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, minmax(240px, 1fr))",
              gap: 12,
            }}
          >
            {GLOBAL_PRICE_KEYS.map((key) => (
              <div key={key} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                <label htmlFor={`global-${key}`}>{formatPriceFieldLabel(key)}</label>
                <input
                  id={`global-${key}`}
                  type="number"
                  min={0}
                  step={1}
                  name={key}
                  value={globalTopLevelPrice[key]}
                  onChange={handleGlobalTopLevelChange}
                  disabled={!hasUpdateAccess || isSavingGlobal}
                  placeholder={globalDefaults?.[key] ?? ""}
                  onKeyDown={blockNonIntegerKeys}
                  style={{
                    padding: "12px",
                    border: `1px solid ${hasUpdateAccess ? "#68d3bd" : "#ccc"}`,
                    borderRadius: "8px",
                    fontSize: "1rem",
                    outline: "none",
                    color: globalTopLevelPrice[key] === "" ? "#999" : "black",
                    backgroundColor: !hasUpdateAccess ? "#f5f5f5" : "white",
                  }}
                />
              </div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
            <button
              onClick={handleGlobalSave}
              disabled={!hasUpdateAccess || isSavingGlobal}
              style={{
                padding: "12px 18px",
                border: "none",
                borderRadius: "10px",
                fontWeight: 700,
                fontSize: "1rem",
                outline: "none",
                transition: "0.3s ease-in-out",
                backgroundColor: hasUpdateAccess ? "#68d3bd" : "#ccc",
                cursor:
                  !hasUpdateAccess || isSavingGlobal ? "not-allowed" : "pointer",
                color: hasUpdateAccess ? "black" : "#666",
                height: 44,
                whiteSpace: "nowrap",
              }}
            >
              {globalDefaultsRecord ? "Update Global Price" : "Create Global Price"}
            </button>
          </div>
        <div style={{ textAlign: "center", marginTop: 8, color: "#777", fontSize: 12 }}>
          Last updated: {globalDefaultsRecord?.updated_by_name || "—"}
        </div>
        </div>
      )}

      {activeTab === "service" && (
        <>
      <div
        style={{
          display: "flex",
          gap: 16,
          alignItems: "flex-end",
          flexWrap: "wrap",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
          <label htmlFor="clientSelect">Client</label>
          <select
            id="clientSelect"
            value={selectedClientId}
            disabled={!hasUpdateAccess || isLoadingClients || isSavingService}
            onChange={(e) => handleClientChange(e.target.value)}
            style={{
              padding: "12px",
              border: `1px solid ${hasUpdateAccess ? "#68d3bd" : "#ccc"}`,
              borderRadius: "8px",
              fontSize: "1rem",
              backgroundColor: hasUpdateAccess ? "white" : "#f5f5f5",
              cursor: !hasUpdateAccess ? "not-allowed" : "pointer",
              width: "100%",
            }}
          >
            <option value="" disabled>
              {isLoadingClients ? "Loading..." : "Select client"}
            </option>
            {clients.map((c) => (
              <option key={c?._id} value={c?._id}>
                {c?.client_name || c?.short_code || c?.client_id}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
          <label htmlFor="primaryService">Primary Service</label>
          <select
            id="primaryService"
            value={selectedPrimaryServiceId}
            disabled={
              !hasUpdateAccess ||
              isLoadingSubcats ||
              isSavingService ||
              isLoadingCenterServices ||
              !selectedClientId ||
              primaryServices.length === 0
            }
            onChange={(e) => {
              const v = e.target.value;
              setSelectedPrimaryServiceId(v);
              safeSetLs(LS.selectedPrimaryServiceId, String(v));
            }}
            style={{
              padding: "12px",
              border: `1px solid ${hasUpdateAccess ? "#68d3bd" : "#ccc"}`,
              borderRadius: "8px",
              fontSize: "1rem",
              backgroundColor: hasUpdateAccess ? "white" : "#f5f5f5",
              cursor: !hasUpdateAccess ? "not-allowed" : "pointer",
              width: "100%",
            }}
          >
            {primaryServices.map((s) => (
              <option key={s._id} value={s._id}>
                {getTitleFromService(s)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div
        style={{
          border: "1px solid #e3e3e3",
          borderRadius: 12,
          padding: 16,
          backgroundColor: "#fff",
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: 12 }}>
          Top-Level Ticket Amounts
        </h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(240px, 1fr))",
            gap: 12,
          }}
        >
          {SERVICE_PRICE_KEYS.map((key) => (
            <div key={key} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label htmlFor={key}>{formatPriceFieldLabel(key)}</label>
              <input
                id={key}
                type="number"
                min={0}
                step={1}
                name={key}
                value={topLevelPrice[key]}
                onChange={handleTopLevelChange}
                disabled={
                  !hasUpdateAccess || isLoadingSubcats || isLoadingRowDefaults || isSavingService
                }
                placeholder={topLevelDefaults?.[key] ?? globalDefaults?.[key] ?? ""}
                onKeyDown={blockNonIntegerKeys}
                style={{
                  padding: "12px",
                  border: `1px solid ${hasUpdateAccess ? "#68d3bd" : "#ccc"}`,
                  borderRadius: "8px",
                  fontSize: "1rem",
                  outline: "none",
                  color: topLevelPrice[key] === "" ? "#999" : "black",
                  backgroundColor: !hasUpdateAccess ? "#f5f5f5" : "white",
                }}
              />
            </div>
          ))}
        </div>
        <div style={{ marginTop: 10, color: "#666", fontSize: 13 }}>
          Leaving a field blank uses the service record default, then the global default when available.
        </div>
        <div style={{ textAlign: "center", marginTop: 8, color: "#777", fontSize: 12 }}>
          Last updated: {serviceLastUpdatedByName || "—"}
        </div>
      </div>

      <div
        style={{
          border: "1px solid #e3e3e3",
          borderRadius: 12,
          padding: 16,
          backgroundColor: "#fff",
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: 12 }}>Sub-Category Pricing</h3>

        {isLoadingSubcats && <div>Loading sub-categories...</div>}
        {!isLoadingSubcats && rows.length === 0 && (
          <div>No sub-categories found for selected primary service.</div>
        )}

        {!isLoadingSubcats && rows.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", borderBottom: "1px solid #eee", padding: "10px 8px" }}>
                    Level 1
                  </th>
                  <th style={{ textAlign: "left", borderBottom: "1px solid #eee", padding: "10px 8px" }}>
                    Level 2
                  </th>
                  <th style={{ textAlign: "left", borderBottom: "1px solid #eee", padding: "10px 8px" }}>
                    Subscribed Price
                  </th>
                  <th style={{ textAlign: "left", borderBottom: "1px solid #eee", padding: "10px 8px" }}>
                    Unsubscribed Price
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, idx) => {
                  return (
                    <tr key={row.category_id || idx}>
                      <td style={{ borderBottom: "1px solid #f2f2f2", padding: "10px 8px" }}>
                        {row.level1}
                      </td>
                      <td style={{ borderBottom: "1px solid #f2f2f2", padding: "10px 8px" }}>
                        {row.level2}
                      </td>
                      <td style={{ borderBottom: "1px solid #f2f2f2", padding: "10px 8px" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          <input
                            type="number"
                            value={row.subscribed_price}
                            min={0}
                            step={1}
                            disabled={
                              !hasUpdateAccess || isLoadingSubcats || isLoadingRowDefaults || isSavingService
                            }
                            onChange={(e) =>
                              handleRowChange(
                                idx,
                                "subscribed_price",
                                e.target.value === "" ? "" : e.target.value
                              )
                            }
                            placeholder=""
                            onKeyDown={blockNonIntegerKeys}
                            style={{
                              padding: "10px",
                              border: `1px solid ${hasUpdateAccess ? "#68d3bd" : "#ccc"}`,
                              borderRadius: 8,
                              outline: "none",
                              color: row.subscribed_price === "" ? "#999" : "black",
                              backgroundColor: !hasUpdateAccess ? "#f5f5f5" : "white",
                              minWidth: 150,
                            }}
                          />
                        </div>
                      </td>
                      <td style={{ borderBottom: "1px solid #f2f2f2", padding: "10px 8px" }}>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          <input
                            type="number"
                            value={row.unsubscribed_price}
                            min={0}
                            step={1}
                            disabled={
                              !hasUpdateAccess || isLoadingSubcats || isLoadingRowDefaults || isSavingService
                            }
                            onChange={(e) =>
                              handleRowChange(
                                idx,
                                "unsubscribed_price",
                                e.target.value === "" ? "" : e.target.value
                              )
                            }
                            placeholder=""
                            onKeyDown={blockNonIntegerKeys}
                            style={{
                              padding: "10px",
                              border: `1px solid ${hasUpdateAccess ? "#68d3bd" : "#ccc"}`,
                              borderRadius: 8,
                              outline: "none",
                              color: row.unsubscribed_price === "" ? "#999" : "black",
                              backgroundColor: !hasUpdateAccess ? "#f5f5f5" : "white",
                              minWidth: 150,
                            }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "center", marginTop: 16 }}>
          <button
            type="button"
            onClick={handleTopLevelSave}
            disabled={
              !hasUpdateAccess ||
              isLoadingSubcats ||
              isLoadingRowDefaults ||
              isSavingService ||
              !selectedPrimaryServiceId ||
              !selectedTitle
            }
            style={{
              padding: "12px 18px",
              border: "none",
              borderRadius: "10px",
              fontWeight: 700,
              fontSize: "1rem",
              outline: "none",
              transition: "0.3s ease-in-out",
              backgroundColor: hasUpdateAccess ? "#68d3bd" : "#ccc",
              cursor:
                !hasUpdateAccess ||
                isLoadingSubcats ||
                isLoadingRowDefaults ||
                isSavingService ||
                !selectedPrimaryServiceId ||
                !selectedTitle
                  ? "not-allowed"
                  : "pointer",
              color: hasUpdateAccess ? "black" : "#666",
              height: 44,
              whiteSpace: "nowrap",
            }}
          >
            {hasExistingTopLevelRecord
              ? "Update Service & Sub-Category Pricing"
              : "Create Service & Sub-Category Pricing"}
          </button>
        </div>

        {isLoadingRowDefaults && (
          <div style={{ marginTop: 10, color: "#666", fontSize: 13 }}>
            Loading default sub-category prices...
          </div>
        )}

      </div>
        </>
      )}
    </div>
  );
}

export default Subscription;
